import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { PREVIEW_TIMEZONE_DATE_FIX, FIX_TIMEZONE_DATE_ISSUES } from '../../graphql/queries';
import { toast } from 'react-hot-toast';

const TimezoneFixTool = () => {
  const [showPreview, setShowPreview] = useState(false);
  const [isFixing, setIsFixing] = useState(false);
  const [selectedJobs, setSelectedJobs] = useState(new Set());

  // Query to preview affected jobs
  const { 
    data: previewData, 
    loading: previewLoading, 
    refetch: refetchPreview 
  } = useQuery(PREVIEW_TIMEZONE_DATE_FIX, {
    skip: !showPreview,
    onError: (error) => {
      toast.error(`Failed to preview timezone fixes: ${error.message}`);
    }
  });

  // Mutation to fix timezone issues
  const [fixTimezoneIssues, { loading: fixLoading }] = useMutation(FIX_TIMEZONE_DATE_ISSUES, {
    onCompleted: (data) => {
      if (data.fixTimezoneDateIssues.success) {
        toast.success(data.fixTimezoneDateIssues.message);
        refetchPreview(); // Refresh the preview
      } else {
        toast.error(`Fix completed with errors: ${data.fixTimezoneDateIssues.message}`);
        if (data.fixTimezoneDateIssues.errors.length > 0) {
          console.error('Fix errors:', data.fixTimezoneDateIssues.errors);
        }
      }
      setIsFixing(false);
    },
    onError: (error) => {
      toast.error(`Failed to fix timezone issues: ${error.message}`);
      setIsFixing(false);
    }
  });

  const handlePreview = () => {
    setShowPreview(true);
    refetchPreview();
  };

  const handleFix = async () => {
    if (!previewData?.previewTimezoneDateFix?.affectedJobs?.length) {
      toast.error('No jobs to fix');
      return;
    }

    const jobsToFix = selectedJobs.size > 0 ? Array.from(selectedJobs) : null;
    const jobCount = jobsToFix ? selectedJobs.size : previewData.previewTimezoneDateFix.totalCount;

    const confirmed = window.confirm(
      `Are you sure you want to fix ${jobCount} ${jobsToFix ? 'selected' : 'all'} jobs? ` +
      'This will update their scheduled dates by adding one day to correct timezone conversion issues.'
    );

    if (!confirmed) return;

    setIsFixing(true);
    try {
      await fixTimezoneIssues({
        variables: {
          jobIds: jobsToFix
        }
      });
    } catch (error) {
      // Error handling is done in onError callback
    }
  };

  const handleSelectAll = () => {
    if (selectedJobs.size === affectedJobs.length) {
      setSelectedJobs(new Set());
    } else {
      setSelectedJobs(new Set(affectedJobs.map(job => job.id)));
    }
  };

  const handleSelectJob = (jobId) => {
    const newSelected = new Set(selectedJobs);
    if (newSelected.has(jobId)) {
      newSelected.delete(jobId);
    } else {
      newSelected.add(jobId);
    }
    setSelectedJobs(newSelected);
  };

  const affectedJobs = previewData?.previewTimezoneDateFix?.affectedJobs || [];

  return (
    <div>

      <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h4 className="text-sm font-medium text-yellow-800">
              Important Notice
            </h4>
            <p className="text-sm text-yellow-700 mt-1">
              This tool fixes jobs that were affected by a timezone conversion bug during bulk assignments. 
              These jobs were stored one day earlier than intended. It will add one day to the scheduled date 
              of affected jobs. Only jobs with manual date overrides that were recently updated will be considered.
            </p>
          </div>
        </div>
      </div>

      <div className="flex space-x-3 mb-6">
        <button
          onClick={handlePreview}
          disabled={previewLoading}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
        >
          {previewLoading ? 'Loading...' : 'Preview Affected Jobs'}
        </button>

        {affectedJobs.length > 0 && (
          <button
            onClick={handleFix}
            disabled={fixLoading || isFixing}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
          >
            {fixLoading || isFixing ? 'Fixing...' : `Fix ${selectedJobs.size > 0 ? selectedJobs.size : affectedJobs.length} Jobs`}
          </button>
        )}
      </div>

      {previewData?.previewTimezoneDateFix && (
        <div className="border rounded-md">
          <div className="px-4 py-3 bg-gray-50 border-b">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-gray-900">
                {previewData.previewTimezoneDateFix.message}
              </h4>
              {affectedJobs.length > 0 && (
                <div className="flex items-center space-x-4">
                  <button
                    onClick={handleSelectAll}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    {selectedJobs.size === affectedJobs.length ? 'Deselect All' : 'Select All'}
                  </button>
                  <span className="text-sm text-gray-500">
                    {selectedJobs.size} of {affectedJobs.length} selected
                  </span>
                </div>
              )}
            </div>
          </div>
          
          {affectedJobs.length > 0 ? (
            <div className="max-h-96 overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={selectedJobs.size === affectedJobs.length && affectedJobs.length > 0}
                        onChange={handleSelectAll}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Property
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Current Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Proposed Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Assigned To
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Updated
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {affectedJobs.map((job) => (
                    <tr key={job.id} className={selectedJobs.has(job.id) ? 'bg-blue-50' : ''}>
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedJobs.has(job.id)}
                          onChange={() => handleSelectJob(job.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {job.propertyName}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded">
                          {job.currentDate}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">
                          {job.proposedDate}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {job.assignedTo || 'Unknown'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {job.updatedAt}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-4 py-8 text-center text-gray-500">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No affected jobs found</h3>
              <p className="mt-1 text-sm text-gray-500">
                All job dates appear to be correct. No timezone fixes are needed.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TimezoneFixTool;
